import React, { useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Stack, Avatar, Divider,
  CircularProgress, Alert, IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import {
  Hotel, DirectionsCar, Warning, CheckCircle, Phone, ReportProblem,
  Refresh,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { loadRunningTrips } from '../../../reduxcomponents/slices/tripManagementSlice';
import dayjs from 'dayjs';

const COLOR_MAP = {
  green:  { bgcolor: 'success.50',  border: 'success.main',  label: 'All Confirmed',        labelColor: 'success' },
  yellow: { bgcolor: 'warning.50',  border: 'warning.main',  label: 'Follow-up Required',   labelColor: 'warning' },
  red:    { bgcolor: 'error.50',    border: 'error.main',    label: 'Critical Issue',        labelColor: 'error' },
};

const RunningTripCard = ({ trip, onEscalate, onCall }) => {
  const color = trip.status_color || 'green';
  const cm = COLOR_MAP[color];
  const openEscalations = trip.escalations?.filter(e => e.status === 'Open') || [];
  const daysIn = trip.travel_date
    ? Math.max(0, dayjs().diff(dayjs(trip.travel_date), 'day'))
    : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        border: '2px solid',
        borderColor: cm.border,
        bgcolor: cm.bgcolor,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{trip.guest_id?.name || 'Guest'}</Typography>
            <Typography variant="caption" color="text.secondary">{trip.destination || '—'}</Typography>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5}>
            <Chip
              label={cm.label}
              color={cm.labelColor}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">Day {daysIn + 1}</Typography>
          </Stack>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Status Flags */}
        <Stack spacing={0.75}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Hotel fontSize="small" color={trip.hotel_confirmed ? 'success' : 'error'} />
              <Typography variant="caption">Hotel</Typography>
            </Stack>
            <Chip
              label={trip.hotel_confirmed ? 'Confirmed' : 'Pending'}
              color={trip.hotel_confirmed ? 'success' : 'error'}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <DirectionsCar fontSize="small" color={trip.driver_confirmed ? 'success' : 'error'} />
              <Typography variant="caption">Driver</Typography>
            </Stack>
            <Chip
              label={trip.driver_confirmed ? 'Confirmed' : 'Pending'}
              color={trip.driver_confirmed ? 'success' : 'error'}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
          </Stack>
        </Stack>

        {/* Escalations */}
        {openEscalations.length > 0 && (
          <Box mt={1.5} p={1} bgcolor="error.50" borderRadius={1} border="1px solid" borderColor="error.light">
            <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
              <ReportProblem color="error" fontSize="small" />
              <Typography variant="caption" color="error.main" fontWeight={700}>
                {openEscalations.length} Open Escalation{openEscalations.length > 1 ? 's' : ''}
              </Typography>
            </Stack>
            {openEscalations.slice(0, 2).map((esc, i) => (
              <Typography key={i} variant="caption" color="error.dark" display="block" noWrap>
                • {esc.description}
              </Typography>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Executives */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.5}>
            {trip.allocation && trip.allocation.length > 0 ? (
              trip.allocation.map((alloc, idx) => (
                <Box key={idx} display="flex" alignItems="center" gap={0.75}>
                  <Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>
                    {alloc.user_id?.fullName?.[0] || '?'}
                  </Avatar>
                  <Typography variant="caption" color="text.secondary">
                    {alloc.user_id?.fullName || 'Unknown'}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="caption" color="text.disabled">Unassigned</Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Call Guest">
              <IconButton size="small" onClick={() => onCall?.(trip)}>
                <Phone fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Raise Escalation">
              <IconButton size="small" color="error" onClick={() => onEscalate?.(trip)}>
                <Warning fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

const RunningTripsMonitor = ({ onEscalate, onCall }) => {
  const dispatch = useDispatch();
  const { runningTrips, loading } = useSelector(s => s.tripManagement);

  useEffect(() => {
    dispatch(loadRunningTrips());
    const interval = setInterval(() => dispatch(loadRunningTrips()), 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const grouped = {
    red:    runningTrips.filter(t => t.status_color === 'red'),
    yellow: runningTrips.filter(t => t.status_color === 'yellow'),
    green:  runningTrips.filter(t => t.status_color === 'green'),
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Running Trips Monitor
          {runningTrips.length > 0 && (
            <Chip label={runningTrips.length} size="small" color="success" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => dispatch(loadRunningTrips())} disabled={loading.running}>
            {loading.running ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={2} mb={2}>
        {[
          { color: 'success', label: 'Green = All Confirmed' },
          { color: 'warning', label: 'Yellow = Follow-up Required' },
          { color: 'error',   label: 'Red = Critical Issue' },
        ].map(({ color, label }) => (
          <Chip key={label} label={label} color={color} size="small" variant="outlined" />
        ))}
      </Stack>

      {loading.running && runningTrips.length === 0 && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      {runningTrips.length === 0 && !loading.running && (
        <Alert severity="info">No running trips at the moment.</Alert>
      )}

      {/* Critical (Red) */}
      {grouped.red.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" color="error.main" fontWeight={700} mb={1}>
            🔴 Critical Issues ({grouped.red.length})
          </Typography>
          <Grid container spacing={2}>
            {grouped.red.map(trip => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={trip._id}>
                <RunningTripCard trip={trip} onEscalate={onEscalate} onCall={onCall} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Follow-up (Yellow) */}
      {grouped.yellow.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" color="warning.dark" fontWeight={700} mb={1}>
            🟡 Follow-up Required ({grouped.yellow.length})
          </Typography>
          <Grid container spacing={2}>
            {grouped.yellow.map(trip => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={trip._id}>
                <RunningTripCard trip={trip} onEscalate={onEscalate} onCall={onCall} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* All Good (Green) */}
      {grouped.green.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="success.dark" fontWeight={700} mb={1}>
            🟢 All Confirmed ({grouped.green.length})
          </Typography>
          <Grid container spacing={2}>
            {grouped.green.map(trip => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={trip._id}>
                <RunningTripCard trip={trip} onEscalate={onEscalate} onCall={onCall} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default RunningTripsMonitor;
